import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Video, KeyRound, ScrollText, PlayCircle, ShieldCheck, Lock, Clock, Feather, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Navbar } from "@/components/Navbar";
import { Certificate } from "@/components/Certificate";
import { printCertificate } from "@/lib/certificate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SAMPLE_TITLE = "For my dearest family";
const SAMPLE_CODE = "MLM-A7KD-9FQP-3XZM";

const steps = [
  { icon: Video, title: "Record 30 seconds", text: "Speak straight to camera. One short, honest message for the person you love." },
  { icon: KeyRound, title: "Get a secret code", text: "We seal your video and give you a unique unlock code — shown to you on screen." },
  { icon: ScrollText, title: "Leave it in your will", text: "Copy the code into your will or hand it to a trusted person to keep safe." },
  { icon: PlayCircle, title: "They unlock & watch", text: "When the time comes, they enter the code and hear from you once more." },
];

// Smoothly tweens the displayed number whenever `value` changes (easeOutCubic).
const AnimatedNumber = ({ value, className }) => {
  const [display, setDisplay] = useState(value);
  const displayRef = useRef(value);
  useEffect(() => {
    const from = displayRef.current;
    const to = value;
    if (from === to) return;
    const duration = 750;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(from + (to - from) * eased);
      displayRef.current = v;
      setDisplay(v);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span data-testid="messages-created-count" className={className}>{display.toLocaleString()}</span>;
};

export default function Landing() {
  const navigate = useNavigate();
  const [showSample, setShowSample] = useState(false);
  const [hasExample, setHasExample] = useState(false);
  const [count, setCount] = useState(32310);

  useEffect(() => {
    axios.get(`${API}/example-video`).then(({ data }) => setHasExample(!!data.exists)).catch(() => {});
    // Count each visitor at most once per day; otherwise just read the current total.
    const today = new Date().toISOString().slice(0, 10);
    const last = localStorage.getItem("mlm_visit_day");
    const base = `${API}/stats/messages-created`;
    const request =
      last !== today
        ? axios.post(`${base}/visit`).then((r) => { localStorage.setItem("mlm_visit_day", today); return r; })
        : axios.get(base);
    request.then(({ data }) => { if (typeof data.count === "number") setCount(data.count); }).catch(() => {});
  }, []);

  // Gentle on-screen tick-up every 10 minutes so the number feels alive.
  useEffect(() => {
    const id = setInterval(() => setCount((c) => c + 1), 600000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="min-h-screen bg-obsidian bg-grain">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-5 sm:px-8 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1640095889747-2090ee12fa7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to bottom, black 20%, transparent 95%)",
            WebkitMaskImage: "linear-gradient(to bottom, black 20%, transparent 95%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto text-center">
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold/80 mb-6 animate-fade-up">
            <Feather className="w-3.5 h-3.5" /> A message that waits for them
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-gold-light text-glow-gold leading-[1.1] animate-fade-up" style={{ animationDelay: "0.1s", opacity: 0 }}>
            Leave words that
            <br /> outlive time.
          </h1>
          <p className="mt-7 text-base sm:text-lg text-slate-300 font-light leading-relaxed max-w-xl mx-auto animate-fade-up" style={{ animationDelay: "0.25s", opacity: 0 }}>
            Record a 30-second video for someone you love. We lock it behind a private code
            that only unlocks when you choose to share it — perfect for your will.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up" style={{ animationDelay: "0.4s", opacity: 0 }}>
            <Button
              data-testid="hero-record-button"
              onClick={() => navigate("/record")}
              className="bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full px-8 h-12 text-base shadow-[0_0_35px_rgba(212,175,55,0.25)]"
            >
              <Video className="w-5 h-5 mr-1" /> Record my message
            </Button>
            <Button
              data-testid="hero-unlock-button"
              onClick={() => navigate("/unlock")}
              variant="outline"
              className="rounded-full px-8 h-12 text-base border-gold/40 text-gold-light hover:bg-gold/10 hover:text-gold bg-transparent"
            >
              <KeyRound className="w-5 h-5 mr-1" /> Unlock a message
            </Button>
          </div>

          {count !== null && (
            <div
              className="mt-9 flex justify-center animate-fade-up"
            >
              <div
                data-testid="messages-created-counter"
                className="inline-flex items-center gap-2.5 rounded-full border border-gold/20 bg-obsidian/50 backdrop-blur-sm px-5 py-2.5 text-sm text-slate-300"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold/60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
                </span>
                <AnimatedNumber
                  value={count}
                  className="font-mono text-gold-light font-semibold tabular-nums"
                />
                messages created
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Process */}
      <section className="relative py-20 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium text-slate-100 text-center mb-4">
          How it works
        </h2>
        <p className="text-slate-400 text-center max-w-lg mx-auto mb-14">
          Four quiet steps. No accounts, no fuss — just your voice, kept safe until it's needed.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div
              key={s.title}
              data-testid={`process-card-${i + 1}`}
              className="glass-card rounded-2xl p-7 hover:-translate-y-1 transition-transform duration-500"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-gold/10 border border-gold/30 text-gold">
                  <s.icon className="w-5 h-5" />
                </span>
                <span className="font-mono text-sm text-gold/60">0{i + 1}</span>
              </div>
              <h3 className="font-sans font-semibold text-lg text-gold-light mb-2">{s.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Example message */}
      {hasExample && (
        <section data-testid="example-section" className="relative py-20 px-5 sm:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold/80 mb-5">
              <PlayCircle className="w-4 h-4" /> See an example
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium text-slate-100 mb-4">
              A message, shared
            </h2>
            <p className="text-slate-400 max-w-lg mx-auto mb-10">
              This is what a last message can look like — a few honest seconds for the people who matter most.
            </p>
            <div className="glass-card rounded-3xl p-4">
              <video
                data-testid="example-video"
                src={`${API}/example-video/stream`}
                controls
                playsInline
                preload="metadata"
                className="w-full rounded-2xl bg-black aspect-video object-contain"
              />
            </div>
          </div>
        </section>
      )}

      {/* Will integration */}
      <section className="relative py-20 px-5 sm:px-8">
        <div className="max-w-5xl mx-auto glass-card rounded-3xl overflow-hidden grid lg:grid-cols-2">
          <div
            className="min-h-[280px] hidden lg:block"
            style={{
              backgroundImage:
                "url('https://images.pexels.com/photos/37354404/pexels-photo-37354404.jpeg?auto=compress&cs=tinysrgb&w=940')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="p-9 sm:p-12">
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-slate-100 mb-5">
              Made to live in your will
            </h2>
            <p className="text-slate-300 leading-relaxed mb-6">
              Once your message is sealed, you'll get an unlock code. Add a simple line to your will:
            </p>
            <div className="font-mono text-sm text-gold-light bg-obsidian/60 border border-gold/20 rounded-xl p-5 leading-relaxed">
              "I direct that my 30-second video message be accessed at this app using
              Unlock Code: <span className="text-gold font-bold">MLM-XXXX-XXXX-XXXX</span>"
            </div>
            <ul className="mt-8 space-y-3">
              {[
                { icon: Lock, t: "Code-only access — no code, no video" },
                { icon: ShieldCheck, t: "Stored securely in a private vault" },
                { icon: Clock, t: "Waits patiently, opens exactly when you choose" },
              ].map((f) => (
                <li key={f.t} className="flex items-center gap-3 text-sm text-slate-300">
                  <f.icon className="w-4 h-4 text-gold shrink-0" /> {f.t}
                </li>
              ))}
            </ul>
            <Button
              data-testid="view-sample-certificate-button"
              onClick={() => setShowSample(true)}
              variant="outline"
              className="mt-8 rounded-full border-gold/40 text-gold-light bg-transparent hover:bg-gold/10 hover:text-gold"
            >
              <FileText className="w-4 h-4 mr-2" /> View sample certificate
            </Button>
          </div>
        </div>
      </section>

      <Dialog open={showSample} onOpenChange={setShowSample}>
        <DialogContent data-testid="sample-certificate-dialog" className="bg-obsidian-soft border-gold/20 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-gold-light text-xl">Sample certificate</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400 -mt-1 mb-2">
            This is what you'll receive after sealing a message — keep it with your will. (Example details shown.)
          </p>
          <Certificate title={SAMPLE_TITLE} code={SAMPLE_CODE} />
          <Button
            data-testid="print-sample-button"
            onClick={() => printCertificate(SAMPLE_TITLE, SAMPLE_CODE)}
            className="w-full mt-2 bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full h-11"
          >
            <FileText className="w-4 h-4 mr-2" /> Print / Save as PDF
          </Button>
        </DialogContent>
      </Dialog>

      <footer className="py-10 text-center text-slate-500 text-sm border-t border-gold/10">
        <p className="font-serif text-gold-light/70 text-base mb-1">My Last Message</p>
        Words kept safe, for when they matter most.
      </footer>
      <div className="pb-6 text-center">
        <button
          data-testid="footer-admin-link"
          onClick={() => navigate("/admin")}
          className="text-xs text-slate-600 hover:text-gold transition-colors underline underline-offset-2"
        >
          Admin
        </button>
      </div>
    </div>
  );
}
